<template>
  <div class="container mx-auto px-4 py-8 max-w-4xl">
    <!-- 로딩 상태 -->
    <div v-if="isLoading" class="animate-pulse">
      <div class="h-8 bg-gray-300 rounded mb-4"></div>
      <div class="h-4 bg-gray-300 rounded mb-8 w-2/3"></div>
      <div class="space-y-4">
        <div class="h-12 bg-gray-300 rounded"></div>
        <div class="h-12 bg-gray-300 rounded"></div>
        <div class="h-32 bg-gray-300 rounded"></div>
      </div>
    </div>

    <!-- 게시글 수정 폼 -->
    <div v-else-if="post" class="space-y-8">
      <!-- 헤더 -->
      <div class="flex items-center space-x-4">
        <button
          @click="$router.go(-1)"
          class="btn btn-outline btn-sm"
        >
          <ArrowLeftIcon class="w-4 h-4 mr-2" />
          뒤로 가기
        </button>
      </div>
      <div class="flex items-center space-x-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">게시글 수정</h1>
          <p class="mt-1 text-gray-600">게시글 정보를 수정하세요</p>
        </div>
      </div>

      <div class="space-y-8">
        <!-- 기본 정보 -->
        <div class="bg-white rounded-lg border shadow-sm p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">기본 정보</h2>
          
          <div class="space-y-6">
            <!-- 제목 -->
            <div>
              <label for="title" class="form-label">제목 *</label>
              <input
                id="title"
                v-model="form.title"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.title }"
                placeholder="예) 강남역 스터디 카페 좌석 예약"
              />
              <p v-if="errors.title" class="form-error">{{ errors.title }}</p>
            </div>

            <!-- 장소명 -->
            <div>
              <label for="place" class="form-label">장소명 및 주소 검색 <span class="text-xs form-error" style="font-size: smaller"> *검색을 통해 목록에서 선택하면 자동으로 입력됩니다 (권장)</span></label>
              <KakaoMap
                  :placeName="form.place"
                  :address="form.address"
                  @select="selectPlace"
              />
            </div>
            <div>
              <label for="place" class="form-label">장소명 *</label>
              <input
                id="place"
                v-model="form.place"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.place }"
                placeholder="예) 강남 스터디 카페"
              />
              <p v-if="errors.place" class="form-error">{{ errors.place }}</p>
            </div>

            <!-- 주소 -->
            <div>
              <label for="address" class="form-label">주소 *</label>
              <input
                id="address"
                v-model="form.address"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.address }"
                placeholder="예) 서울시 강남구 강남대로 123"
              />
              <p v-if="errors.address" class="form-error">{{ errors.address }}</p>
            </div>

            <!-- 이용 기간 -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="startDate" class="form-label">시작일 *</label>
                <input
                  id="startDate"
                  v-model="form.startDate"
                  type="datetime-local"
                  required
                  class="form-input"
                  :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.startDate }"
                />
                <p v-if="errors.startDate" class="form-error">{{ errors.startDate }}</p>
              </div>
              <div>
                <label for="endDate" class="form-label">종료일 *</label>
                <input
                  id="endDate"
                  v-model="form.endDate"
                  type="datetime-local"
                  required
                  class="form-input"
                  :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.endDate }"
                />
                <p v-if="errors.endDate" class="form-error">{{ errors.endDate }}</p>
              </div>
            </div>
            <div>
              <label for="maxSeatsPerUser" class="form-label">1인당 예약 가능 좌석 수 *</label>
              <input
                id="maxSeatsPerUser"
                v-model.number="form.maxSeatsPerUser"
                type="number"
                min="1"
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.maxSeatsPerUser }"
                placeholder="예) 1"
              />
              <p v-if="errors.maxSeatsPerUser" class="form-error">{{ errors.maxSeatsPerUser }}</p>
            </div>
          </div>
        </div>

        <!-- 내용 -->
        <div class="bg-white rounded-lg border shadow-sm p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">상세 설명</h2>
          
          <div>
            <label for="content" class="form-label">내용 *</label>
            <textarea
              id="content"
              v-model="form.content"
              required
              rows="8"
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.content }"
              placeholder="장소에 대한 자세한 설명을 작성해주세요."
            ></textarea>
            <p v-if="errors.content" class="form-error">{{ errors.content }}</p>
          </div>
        </div>

        <!-- 좌석 설정 (수정 불가) -->
        <div class="bg-white rounded-lg border shadow-sm p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">좌석 설정</h2>
          
          <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div class="flex items-center">
              <ExclamationTriangleIcon class="w-5 h-5 text-yellow-400 mr-2" />
              <p class="text-yellow-800 text-sm">
                예약이 있는 경우 좌석 설정을 변경할 수 없습니다.
              </p>
            </div>
          </div>

          <div>
            <label class="form-label">현재 좌석 배치</label>
            <div class="mt-2 p-4 bg-gray-50 rounded-lg">
              <SeatLayout
                  :key="seatLayoutKey"
                  :mode="'Edit'"
                  :layout="form.seatLayoutObj"
                  :reserved-seat-numbers="reservedSeatNumbers"
                  :has-active-reservations="reservedSeatNumbers.length > 0"
                  :readonly="reservedSeatNumbers.length > 0"
                  @handleSeatLayout="handleSeatLayout"
              />
            </div>
            <p class="form-hint mt-2">총 {{ getTotalSeats() }}개의 좌석</p>
          </div>
        </div>

        <!-- 에러 메시지 -->
        <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-md p-4">
          <p class="text-red-600 text-sm">{{ errors.general }}</p>
        </div>

        <!-- 액션 버튼 -->
        <div class="flex justify-end space-x-4">
          <RouterLink :to="`/posts/${postId}`" class="btn btn-outline btn-sm">
            취소
          </RouterLink>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="btn btn-primary btn-sm"
            :class="{ 'opacity-50 cursor-not-allowed': isSubmitting }"
            v-on:click="handleSubmit"
          >
            <span v-if="isSubmitting" class="flex items-center">
              <div class="spinner w-4 h-4 mr-2"></div>
              수정 중...
            </span>
            <span v-else>수정 완료</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 에러 상태 -->
    <div v-else class="text-center py-16">
      <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <ExclamationTriangleIcon class="w-8 h-8 text-gray-400" />
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">게시글을 찾을 수 없습니다</h3>
      <p class="text-gray-600 mb-6">삭제되었거나 수정 권한이 없는 게시글입니다.</p>
      <RouterLink to="/posts" class="btn btn-primary btn-sm">
        목록으로 돌아가기
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import {postApi, reservationApi} from '@/services/api'
import type {Post, PostRequest } from '@/types'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import SeatLayout from '@/components/SeatLayout.vue'
import {deserializeSeatStatus, serializeSeatStatus} from "@/utils";
import KakaoMap from "@/components/KakaoMap.vue";

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const user = computed(() => authStore.user)

const post = ref<Post | null>(null)
const isLoading = ref(true)
const isSubmitting = ref(false)
const seatLayoutKey = ref(0)
const reservedSeatNumbers = ref<number[]>([])

const postId = computed(() => parseInt(route.params.id as string))

// 폼 데이터
const form = reactive<PostRequest>({
  title: '',
  content: '',
  place: '',
  address: '',
  seatLayout: '',
  seatLayoutObj: {
    rows: 0,
    seatsPerRow: 0,
    totalSeats: 0,
    seatStatus: [[]]
  },
  startDate: '',
  endDate: '',
  maxSeatsPerUser: 1
})

// 에러 상태
const errors = reactive({
  title: '',
  content: '',
  place: '',
  address: '',
  startDate: '',
  endDate: '',
  maxSeatsPerUser: '',
  general: ''
})

// 총 좌석 수 얻기
const getTotalSeats = () => {
  try {
    const layoutData = JSON.parse(form.seatLayout)
    return layoutData.totalSeats || 0
  } catch {
    return 0
  }
}

// 게시글 로드
const loadPost = async () => {
  try {
    isLoading.value = true
    const response = await postApi.getPost(postId.value)
    post.value = response.data

    // 수정 권한 확인
    if (!user.value ||
        (user.value.role !== 'ADMIN' && user.value.id !== post.value.user?.id)) {
      post.value = null
      return
    }
    
    // 폼에 데이터 설정
    form.title = post.value.title
    form.content = post.value.content
    form.place = post.value.place
    form.address = post.value.address
    form.seatLayout = post.value.seatLayout
    form.maxSeatsPerUser = post.value.maxSeatsPerUser || 1

    const seatStatus: number[][] = deserializeSeatStatus(post.value.seatLayout)
    const rows: number = seatStatus.length
    const seatsPerRow: number = seatStatus[0].length
    let totalSeats: number = 0;
    seatStatus.forEach(x => x.forEach(y => { if (y == 1) { totalSeats++ } }))
    form.seatLayoutObj.rows = rows
    form.seatLayoutObj.seatsPerRow = seatsPerRow
    form.seatLayoutObj.totalSeats = totalSeats
    form.seatLayoutObj.seatStatus = seatStatus
    
    // 날짜 포맷 조정
    form.startDate = new Date(post.value.startDate).toISOString().slice(0, 16)
    form.endDate = new Date(post.value.endDate).toISOString().slice(0, 16)

    // 예약 정보도 로드
    await Promise.all([
      loadSeatStatus()
    ])

  } catch (error: any) {
    console.error('Failed to load post:', error)
    if (error.response?.status === 404) {
      post.value = null
    } else {
      toast.error('게시글을 불러오는데 실패했습니다.')
    }
  } finally {
    isLoading.value = false
  }
}

// 예약 좌석 현황 로드
const loadSeatStatus = async () => {
  try {
    const response = await reservationApi.getPostSeatStatus(postId.value)
    reservedSeatNumbers.value = response.data.reservedSeatNumbers || []
  } catch (error) {
    console.error('Failed to load seat status:', error)
  }
}

// 에러 초기화
const clearErrors = () => {
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })
}

// 카카오맵 위치 선택 이벤트
const selectPlace = (place: any) => {
  form.place = place.place_name
  form.address = place.road_address_name || place.address_name
}

// 폼 유효성 검사
const validateForm = (): boolean => {
  clearErrors()
  let isValid = true

  // 제목 검증
  if (!form.title.trim()) {
    errors.title = '제목을 입력해주세요.'
    isValid = false
  } else if (form.title.length > 100) {
    errors.title = '제목은 100자 이하로 입력해주세요.'
    isValid = false
  }

  // 장소명 검증
  if (!form.place.trim()) {
    errors.place = '장소명을 입력해주세요.'
    isValid = false
  }

  // 주소 검증
  if (!form.address.trim()) {
    errors.address = '주소를 입력해주세요.'
    isValid = false
  }

  // 내용 검증
  if (!form.content.trim()) {
    errors.content = '내용을 입력해주세요.'
    isValid = false
  } else if (form.content.length < 10) {
    errors.content = '내용은 10자 이상 입력해주세요.'
    isValid = false
  }

  // 날짜 검증
  if (!form.startDate) {
    errors.startDate = '시작일을 선택해주세요.'
    isValid = false
  }

  if (!form.endDate) {
    errors.endDate = '종료일을 선택해주세요.'
    isValid = false
  }

  if (!form.maxSeatsPerUser || form.maxSeatsPerUser < 1) {
    errors.maxSeatsPerUser = '1명 이상 예약 가능하도록 설정해주세요.'
    isValid = false
  }

  if (form.startDate && form.endDate) {
    const startDate = new Date(form.startDate)
    const endDate = new Date(form.endDate)

    if (endDate <= startDate) {
      errors.endDate = '종료일은 시작일보다 이후여야 합니다.'
      isValid = false
    }
  }

  return isValid
}

const handleSeatLayout = (seatLayout: PostRequest['seatLayoutObj']) => {
  form.seatLayoutObj = seatLayout
  form.seatLayout = serializeSeatStatus(seatLayout.seatStatus)
}

// 폼 제출
const handleSubmit = async () => {
  if (!validateForm() || !post.value) return

  try {
    isSubmitting.value = true
    await postApi.updatePost(post.value.id, form)
    
    toast.success('게시글이 수정되었습니다!')
    router.push(`/posts/${post.value.id}`)
  } catch (error: any) {
    console.error('Failed to update post:', error)
    if (error.response?.data?.message) {
      errors.general = error.response.data.message
    } else if (error.response?.status === 400) {
      errors.general = '입력 정보를 확인해주세요.'
    } else if (error.response?.status === 403) {
      errors.general = '수정 권한이 없습니다.'
    } else {
      errors.general = '게시글 수정에 실패했습니다. 다시 시도해주세요.'
    }
    toast.error('게시글 수정에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  loadPost()
})
</script>
